'use client';

import React, { useState, useCallback, useMemo } from 'react';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import {
  Button,
  Card,
  CardContent,
  Input,
  Checkbox,
  Tooltip,
} from 'src/components/PulseUI';
import {
  DeactivateButton,
  DeactivateDialog,
} from 'src/components/Settings/Companies/components';
import { ImpersonateButton } from '../ImpersonateButton';
import { ResendInviteButton } from '../ResendInviteButton';
import { PasswordResetButton } from '../PasswordResetButton';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useAuthorization } from 'src/hooks/authorization';
import {
  useUpdateCompanyUserMutation,
  useGetCompanyUserQuery,
} from 'src/api/redux';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import {
  InternalViewBadge,
  PageHeader,
} from 'src/components/Settings/components';
import { AccessTags, UserStatusDot } from '../UserTable/components';
import { LAST_ACTIVE_TOOLTIP, STATUS_CONFIG } from '../UserTable/constants';
import {
  formatLastActive,
  getAccessTags,
  getUserStatus,
} from '../UserTable/utils';

// Integration Admin is deliberately absent: it is a legacy flag now covered by
// the permission model, so it is no longer editable here. The update endpoint
// leaves a field it isn't sent alone, so omitting it preserves whatever a user
// already holds.
const permissions = [
  { label: 'Is Manager', value: 'is_manager' },
  // LIR eligibility only feeds MedScout's own reporting, so it reads as noise
  // in the customer-facing view (MKT-382).
  { label: 'Is LIR Eligible', value: 'is_lir_eligible', internalOnly: true },
];

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  is_manager: boolean;
  is_lir_eligible: boolean;
}

const EMAIL_ERROR_PREFIX = 'email: ';

// Ties the visual label and the refusal message to the input, so assistive
// technology gets the same name and error state a sighted user sees.
const EMAIL_INPUT_ID = 'user-card-email';
const EMAIL_ERROR_ID = 'user-card-email-error';

interface RefusalErrors {
  /** Message to render against the Email input, or null when nothing named that field. */
  emailError: string | null;
  /** Message for the notification banner, or null when the input error covers it all. */
  bannerMessage: string | null;
}

/**
 * Splits a rejected `updateCompanyUser` into a field-level message and a banner message.
 *
 * The API flattens DRF validation errors into a single `details` string whose lines are each
 * prefixed with the offending field name, and carries the field attribution separately in `code`
 * (DRF's `get_codes()`) as a field-keyed map. Every other refusal — a 403, a 404, a project
 * exception such as the direct-reports guard, a 5xx, a network failure — puts a plain string in
 * `code` or omits it, and those go to the banner whole.
 *
 * A refusal can name several fields at once, so both halves are returned: the email line renders
 * against the input, and anything else still reaches the banner rather than being dropped.
 */
const partitionRefusal = (error: unknown): RefusalErrors => {
  const body = (
    error as {
      data?: {
        code?: unknown;
        details?: string;
        error_message?: string;
      };
    }
  )?.data;

  const code = body?.code;
  const isFieldKeyed =
    typeof code === 'object' && code !== null && !Array.isArray(code);

  if (!isFieldKeyed) {
    return {
      emailError: null,
      bannerMessage: body?.error_message || null,
    };
  }

  const lines = String(body?.details ?? '')
    .split('\n')
    .filter((line) => line.trim() !== '');
  const emailLines = lines.filter((line) =>
    line.startsWith(EMAIL_ERROR_PREFIX)
  );
  const otherLines = lines.filter(
    (line) => !line.startsWith(EMAIL_ERROR_PREFIX)
  );

  const hasEmailError = 'email' in (code as Record<string, unknown>);

  return {
    emailError: hasEmailError
      ? emailLines
          .map((line) => line.slice(EMAIL_ERROR_PREFIX.length))
          .join(' ')
          .trim() || 'Enter a valid email address.'
      : null,
    // Whatever the input cannot show still has to reach the user.
    bannerMessage: otherLines.length > 0 ? otherLines.join('\n') : null,
  };
};

interface AvatarProps {
  src?: string;
  alt: string;
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({ src, alt, className = '' }) => {
  return (
    <div
      className={`relative rounded-full overflow-hidden bg-neutral-100 ${className}`}
    >
      {src ? (
        <Image
          src={src}
          alt={alt}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-neutral-200 text-neutral-600">
          {alt.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
};

const UserCard = () => {
  const log = useLogging();
  const { userManagementCapabilities } = useAuthorization();
  const { canWrite, canDeactivate, isStaff, canImpersonate } =
    userManagementCapabilities;

  const { setNotification } = useNotification();
  const { showConfirmDeleteModal, setShowConfirmDeleteModal } = useDashboard();

  // Get IDs from route params
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();

  const { data: currentUser, refetch: refetchUser } = useGetCompanyUserQuery(
    { companyId, userId },
    { skip: !companyId || !userId }
  );

  const [updateCompanyUser] = useUpdateCompanyUserMutation();

  // Track only the changes/overrides, not the full form state
  const [formChanges, setFormChanges] = useState<Partial<FormData>>({});

  // Get current form values by merging currentUser data with changes
  const getFormValue = (field: keyof FormData) => {
    if (field in formChanges) {
      return formChanges[field];
    }

    // Return current user data as fallback
    switch (field) {
      case 'first_name':
        return currentUser?.first_name || '';
      case 'last_name':
        return currentUser?.last_name || '';
      // TODO(MKT-596): drop the `username` fallback once the API returns
      // `email` on this response. The service copies a new address into
      // `username`, so reading it keeps the display correct until then.
      case 'email':
        return currentUser?.email || currentUser?.username || '';
      case 'is_manager':
        return currentUser?.permissions?.is_manager || false;
      case 'is_lir_eligible':
        return currentUser?.is_lir_eligible || false;
      default:
        return '';
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  // Field-level refusal from the API, rendered against the Email input rather
  // than as a banner so the administrator sees which value was rejected.
  const [emailError, setEmailError] = useState<string | null>(null);

  // MedScout-internal flags stay out of the customer-facing view (MKT-382).
  const visiblePermissions = useMemo(
    () =>
      permissions.filter((permission) => isStaff || !permission.internalOnly),
    [isStaff]
  );

  const accessTags = useMemo(() => getAccessTags(currentUser), [currentUser]);

  const status = getUserStatus(currentUser);
  const canResendInvite = canWrite && status === 'invited';

  // Component is dirty if there are any form changes
  const isDirty = Object.keys(formChanges).length > 0;
  const [copied, setCopied] = useState(false);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      if (name === 'email') {
        setEmailError(null);
      }
      setFormChanges((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleCheckboxChange = useCallback(
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormChanges((prev) => ({ ...prev, [name]: e.target.checked }));
    },
    []
  );

  const handleConfirmDeleteModalClose = () => {
    setShowConfirmDeleteModal(false);
  };

  const handleCopyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getFormValue('email') as string);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  }, [currentUser?.email, currentUser?.username, formChanges.email]);

  const onSubmit = async () => {
    if (!userId || !companyId) {
      setNotification({
        title: 'Error',
        message: 'Missing required user or company information',
        type: 'error',
      });
      return;
    }

    setIsLoading(true);
    setEmailError(null);
    try {
      // Build the final form data by merging changes with current user data
      const finalFormData = {
        first_name: getFormValue('first_name'),
        last_name: getFormValue('last_name'),
        email: getFormValue('email'),
        is_manager: getFormValue('is_manager'),
        is_lir_eligible: getFormValue('is_lir_eligible'),
      };

      const result = await updateCompanyUser({
        companyId,
        userId,
        ...finalFormData,
      });

      // RTK Query resolves rather than throws without `unwrap()`, so a refusal
      // arrives here — not in the catch below.
      if (result.error) {
        // Deliberately not captured as an exception. These are expected,
        // user-correctable refusals (a duplicate address, a bad phone number);
        // reporting them would be noise, and the base query already captures
        // 500s. The catch below still reports a genuine throw.
        const { emailError: fieldError, bannerMessage } = partitionRefusal(
          result.error
        );

        if (fieldError) {
          setEmailError(fieldError);
        }
        // Banner whenever anything is left over — and always when nothing was
        // attributed to a field, so a refusal is never silently swallowed.
        if (bannerMessage || !fieldError) {
          setNotification({
            title: 'Error',
            message: bannerMessage || 'Failed to update user',
            type: 'error',
          });
        }
        return;
      }

      const newUser = result.data;
      if (newUser) {
        refetchUser(); // Refresh user data
      }

      log.event('User updated', {
        source: 'UserProfile',
        data: finalFormData,
      });
      setNotification({
        title: 'Success',
        message: 'User updated successfully',
        type: 'success',
      });

      // Clear form changes after successful save
      setFormChanges({});
    } catch (err) {
      log.exception('Error updating user', {
        tags: {
          source: 'UserProfile',
          error: err,
        },
      });

      setNotification({
        title: 'Error',
        message: err?.error_message || 'Failed to update user',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Action Header */}
      <PageHeader className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-neutral-900">
            User Profile
          </h2>
          {/* Replaces the old "Admin View" pill — same gate, but it now says
              plainly that this is MedScout-internal (MKT-382). */}
          {isStaff && <InternalViewBadge />}
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <PasswordResetButton userId={currentUser.id?.toString()} />
          )}
          {/* Only an invited user has an invite outstanding to resend — matches
              the row actions in the user table (MKT-383). */}
          {canResendInvite && (
            <ResendInviteButton userId={currentUser.id?.toString()} />
          )}
          {canImpersonate && (
            <ImpersonateButton userId={currentUser.id?.toString()} />
          )}
        </div>
      </PageHeader>

      <Card className="relative">
        <CardContent className="p-6">
          <div className="flex flex-col gap-6">
            {/* At-a-glance summary, mirroring what the user table row shows */}
            <div
              className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-neutral-200 bg-neutral-050 px-3 py-2"
              data-testid="user-summary"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-neutral-600">
                  Status
                </span>
                <UserStatusDot status={status} />
                <span className="text-sm text-neutral-900">
                  {STATUS_CONFIG[status]?.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {/* The figure needs its caveat travelling with it: this is the
                    last day they used MedScout, and it lags by up to a day
                    (MKT-526, MKT-557). */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-neutral-600">
                    Last active
                  </span>
                  {/* The icon alone is the trigger, so the bubble centres over
                      it rather than over the label beside it. */}
                  <Tooltip
                    content={LAST_ACTIVE_TOOLTIP}
                    slotProps={{
                      contentClassName: 'max-w-xs whitespace-normal text-left',
                    }}
                  >
                    <span
                      className="inline-flex"
                      role="img"
                      aria-label={LAST_ACTIVE_TOOLTIP}
                      data-testid="user-summary-last-active-info-icon"
                    >
                      <Icon
                        icon={faCircleInfo}
                        className="text-xs text-neutral-400 hover:text-neutral-600"
                        aria-hidden="true"
                      />
                    </span>
                  </Tooltip>
                </div>
                <span
                  className="text-sm text-neutral-900"
                  data-testid="user-summary-last-active"
                >
                  {formatLastActive(currentUser?.last_seen_at)}
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-xs font-medium text-neutral-600">
                  Access
                </span>
                <AccessTags tags={accessTags} />
              </div>
            </div>

            {/* Profile Header */}
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex flex-col items-center gap-2">
                <Avatar
                  src={(currentUser as any).profilePicture}
                  alt={`${currentUser.first_name} ${currentUser.last_name}`}
                  className="w-28 h-28"
                />
                {isStaff && (
                  <div
                    className="text-sm text-neutral-500 pb-2"
                    data-testid="internal-user-id"
                  >
                    User id: {currentUser.id}
                  </div>
                )}
              </div>
              {/* Name and Email Section */}
              <div className="flex-1 flex flex-col gap-2 max-w-xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-neutral-600">
                      First name
                    </label>
                    <Input
                      name="first_name"
                      value={getFormValue('first_name') as string}
                      onChange={handleInputChange}
                      disabled={!canWrite}
                      className="w-full rounded-full"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-neutral-600">
                      Last name
                    </label>
                    <Input
                      name="last_name"
                      value={getFormValue('last_name') as string}
                      onChange={handleInputChange}
                      disabled={!canWrite}
                      className="w-full rounded-full"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium text-neutral-600"
                    htmlFor={EMAIL_INPUT_ID}
                  >
                    Email
                  </label>
                  <div className="relative">
                    <Input
                      id={EMAIL_INPUT_ID}
                      name="email"
                      type="email"
                      value={getFormValue('email') as string}
                      onChange={handleInputChange}
                      disabled={!canWrite}
                      error={Boolean(emailError)}
                      aria-invalid={Boolean(emailError)}
                      aria-describedby={emailError ? EMAIL_ERROR_ID : undefined}
                      className="w-full rounded-full pr-12"
                      data-testid="email-input"
                    />
                    <button
                      onClick={handleCopyEmail}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 transition-colors"
                      title="Copy email address"
                      data-testid="copy-email-button"
                    >
                      <Icon
                        icon={copied ? faCheck : faCopy}
                        className={
                          copied ? 'text-green-500' : 'text-neutral-500'
                        }
                      />
                    </button>
                  </div>
                  {emailError && (
                    <p
                      id={EMAIL_ERROR_ID}
                      role="alert"
                      className="text-red-500 text-xs mt-1"
                      data-testid="email-error"
                    >
                      {emailError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium text-neutral-600">
                User Permissions
              </h3>
              <div className="bg-neutral-50 rounded-lg divide-y divide-neutral-200 border border-neutral-200">
                {visiblePermissions?.map((permission, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2 first:rounded-t-lg last:rounded-b-lg hover:bg-neutral-100 transition-colors"
                  >
                    <span className="text-sm text-neutral-700">
                      {permission.label}
                    </span>
                    <Checkbox
                      checked={
                        getFormValue(
                          permission.value as keyof FormData
                        ) as boolean
                      }
                      onChange={handleCheckboxChange(permission.value)}
                      disabled={!canWrite}
                      size="sm"
                      data-testid={`permission-checkbox-${permission.value}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center border-t border-neutral-200 pt-4">
              {canDeactivate ? (
                <DeactivateButton selectedUser={currentUser} />
              ) : (
                <span />
              )}
              {canWrite && (
                <Button
                  loading={isLoading}
                  onClick={onSubmit}
                  variant="solid"
                  color="primary"
                  disabled={!isDirty}
                  data-testid="update-button"
                >
                  Update
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <DeactivateDialog
        open={showConfirmDeleteModal || false}
        onClose={handleConfirmDeleteModalClose}
      />
    </div>
  );
};

export default UserCard;
